/**
 * @file Components/Sections/CampusAmbassador.jsx
 * @description Compact, visually appealing Campus Ambassador section.
 * Single-row tier badges + benefits in a sleek card layout.
 */

import React from "react";
import { motion } from "framer-motion";
import { Award, Gem, Crown, Star, ArrowRight, Gift, Percent, Plane, Trophy } from "lucide-react";
import { useSiteSettings } from "../../Context/SiteSettingsContext";

const tiers = [
  { label: 'Silver', icon: Award, req: '2-9 groups', commission: '5%', color: 'from-gray-300 to-gray-400', text: 'text-gray-600', bg: 'bg-gray-50' },
  { label: 'Gold', icon: Star, req: '10-24 groups', commission: '7%', perk: 'Free Kashmir Trip', color: 'from-amber-400 to-yellow-500', text: 'text-amber-700', bg: 'bg-amber-50' },
  { label: 'Diamond', icon: Gem, req: '25-99 groups', commission: '10%', perk: 'International Trip', color: 'from-blue-400 to-indigo-500', text: 'text-blue-700', bg: 'bg-blue-50' },
  { label: 'Platinum', icon: Crown, req: '100+ groups', commission: '15%', perk: 'Annual Int\'l Trip + 5 Domestic', color: 'from-gray-600 to-gray-800', text: 'text-gray-800', bg: 'bg-gray-100' },
];

const CampusAmbassador = () => {
  const { settings } = useSiteSettings();
  const ca = settings?.campusAmbassador || {};
  if (ca.enabled === false) return null;

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative bg-gradient-to-br from-[#0f1b4d] via-[#1a2a6c] to-[#2d3d9e] rounded-3xl overflow-hidden"
        >
          {/* Decorative */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-60 h-60 bg-indigo-400/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />

          <div className="relative p-8 md:p-12">
            {/* Top Row: Info + CTA */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Trophy size={18} className="text-amber-400" />
                  <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">Earn While You Travel</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  {ca.heading || 'Campus Ambassador Program'}
                </h2>
                <p className="text-white/50 text-sm mt-2 max-w-lg">
                  {ca.description || 'Represent Nikaba Travels on your campus. Earn commissions, free trips, swag kits, and certificates.'}
                </p>
              </div>
              <a
                href={ca.formLink || 'https://forms.gle/Raxb19mxFZXLvffV8'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 px-8 py-3.5 bg-white text-[#243185] font-bold text-sm rounded-full hover:shadow-xl hover:scale-105 transition-all flex items-center gap-2"
              >
                Apply Now <ArrowRight size={16} />
              </a>
            </div>

            {/* Quick Benefits */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
              {[
                { icon: Gift, text: 'Branded Swag Kit', sub: 'T-shirt, Pen, Bottle' },
                { icon: Percent, text: 'Up to 15% Commission', sub: 'On every booking' },
                { icon: Plane, text: 'Free Trips', sub: 'Kashmir & International' },
                { icon: Award, text: 'Certificate', sub: 'LinkedIn-ready credential' },
              ].map((b, i) => (
                <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center hover:bg-white/10 transition-colors">
                  <b.icon size={22} className="text-white/70 mx-auto mb-2" />
                  <p className="text-white text-sm font-semibold">{b.text}</p>
                  <p className="text-white/40 text-[11px] mt-0.5">{b.sub}</p>
                </div>
              ))}
            </div>

            {/* Tier Badges */}
            <div>
              <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-4">Membership Tiers</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {tiers.map((tier, i) => {
                  const Icon = tier.icon;
                  return (
                    <motion.div
                      key={tier.label}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className={`${tier.bg} rounded-xl p-4 border border-white/10 hover:scale-[1.03] transition-transform cursor-default`}
                    >
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tier.color} flex items-center justify-center mb-3 shadow-sm`}>
                        <Icon size={18} className="text-white" />
                      </div>
                      <h4 className={`text-sm font-bold ${tier.text}`}>{tier.label}</h4>
                      <p className="text-gray-500 text-[11px] mt-0.5">{tier.req} of 10+ people</p>
                      <div className="mt-3 pt-3 border-t border-gray-200/50">
                        <p className="text-xs font-bold text-gray-700">{tier.commission} commission</p>
                        {tier.perk && <p className="text-[11px] text-gray-500 mt-0.5">{tier.perk}</p>}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CampusAmbassador;
